import { Formik } from "formik";
import React from "react";
import { Form } from "react-bootstrap";
import { Button } from '@blueprint/ui';
import * as yup from "yup";

import { FormFooter, PasswordField } from "../../../common/components/form";
import { FormSubmissionHandler } from "../../../types/common";

import PublicFormContainer from "./PublicFormContainer";

export type ResetPasswordFormValues = {
    password: string;
    confirmedPassword: string;
};

export enum ResetPasswordMode {
    Create,
    Reset,
}

const schema: yup.SchemaOf<ResetPasswordFormValues> = yup.object().shape({
    password: yup
        .string()
        .required()
        .matches(
            /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9|\W]).{8,}$/,
            "Password does not meet the criteria",
        ),
    confirmedPassword: yup
        .string()
        .required()
        .test(
            "is-same-password",
            "Passwords do not match",
            (value, context) => {
                return value === context.parent.password;
            },
        ),
});

type ResetPasswordFormProps = {
    onSubmit: FormSubmissionHandler<ResetPasswordFormValues>;
    mode?: ResetPasswordMode;
};

const ResetPasswordForm = ({
    onSubmit,
    mode = ResetPasswordMode.Reset,
}: ResetPasswordFormProps): JSX.Element => {
    return (
        <PublicFormContainer>
            <h2 className="mb-4">
                {mode === ResetPasswordMode.Reset
                    ? "Reset Password"
                    : "Create user account"}
            </h2>
            <Formik
                validationSchema={schema}
                validateOnBlur={false}
                validateOnChange={false}
                onSubmit={onSubmit}
                initialValues={{
                    password: "",
                    confirmedPassword: "",
                }}
            >
                {({ handleSubmit, isSubmitting }) => (
                    <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                        <PasswordField
                            name="password"
                            label="New Password"
                            autoFocus
                            required={true}
                        />
                        <PasswordField
                            name="confirmedPassword"
                            label="Confirm New Password"
                            required={true}
                        />
                        <ul id="passwordPolicy">
                            <li>Minimum length = 8 characters</li>
                            <li>Case sensitive</li>
                            <li>
                                Password must contain upper case & lower case
                                letters
                            </li>
                            <li>
                                Password must contain at least one number or
                                special character (!@#$%, etc.)
                            </li>
                        </ul>
                        <FormFooter className="d-grid mb-5">
                            <Button
                                type="submit"
                                variant="light.primary"
                                disabled={isSubmitting}
                            >
                                Confirm
                            </Button>
                        </FormFooter>
                    </Form>
                )}
            </Formik>
        </PublicFormContainer>
    );
};

export default ResetPasswordForm;
